"use client";
import React, { useMemo } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { format } from "date-fns";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import "katex/dist/katex.min.css";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import { Info, RotateCw } from "lucide-react";
import { v7 as uuidv7 } from "uuid";
import { CollapsibleMessageContent } from "@/app/[locale]/c/[id]/component/CollapsibleMessageContent";
import { ChatAttachments } from "@/app/[locale]/c/[id]/component/ChatAttachments";
import { CopyContent } from "@/app/[locale]/c/[id]/component/CopyContent";
import { MessageFeedback } from "@/app/[locale]/c/[id]/component/MessageFeedback";
import { ExpandedQueryDebug } from "@/app/[locale]/c/[id]/component/ExpandedQueryDebug";
import InlinePlaceholder from "@/app/[locale]/c/[id]/component/InlinePlaceholder";
import { cn, mapUrlToDomain } from "@/app/lib/utils";
import { preprocessMathContent } from "@/app/lib/markdownPreprocessor";
import { useAuth } from "@/app/hooks";
import { ChatMessageMetadata } from "@/app/[locale]/c/[id]/component/ChatMessageMetadata";
import type { ChatMessage, PlaceholderRegistry } from "@/types";
import { FUNCTION_NAME_ENUM, PROJECTS } from "@/app/lib/enums";
import { ATTACHMENT_ONLY_PROMPT } from "@/app/lib/constants";
import { useAiSourceSelector } from "@/app/store/ai-source-selector";
import { useStateHistoryChatPage } from "@/app/store/chat";
import { Button } from "@/app/components/ui";

const syntaxTheme = {
   ...vscDarkPlus,
   'pre[class*="language-"]': {
      ...vscDarkPlus['pre[class*="language-"]'],
      background: "transparent",
      margin: 0,
      padding: 0
   }
} as typeof vscDarkPlus;

const MarkdownImage = ({ src, alt, ...props }: any) => {
   const [hasError, setHasError] = React.useState(false);
   const url = (src as string) || "";
   if (!url) return null;
   const isExternalUrl = url.startsWith("http://") || url.startsWith("https://");
   const mappedUrl = isExternalUrl ? mapUrlToDomain(url) : url;
   if (hasError) {
      return <b>{alt || mappedUrl}</b>;
   }

   if (isExternalUrl) {
      return (
         <img
            src={mappedUrl}
            alt={alt || "Image"}
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            onError={() => setHasError(true)}
            className="max-w-full h-auto max-h-[560px] object-contain rounded-md my-2"
            {...props}
         />
      );
   }

   return (
      <div className="relative max-w-full my-2">
         <Image
            src={mappedUrl}
            alt={alt || "Image"}
            width={0}
            height={0}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px)"
            className="w-auto h-auto max-w-full max-h-[560px] object-contain rounded-md"
            loading="lazy"
            onError={() => setHasError(true)}
            {...props}
         />
      </div>
   );
};

const MarkdownComponents = {
   code({ className, children, ...props }: any) {
      const match = /language-(\w+)/.exec(className || "");
      return !className?.includes("language-") ? (
         <code
            className={cn("rounded-md bg-muted px-1 py-0.5 font-mono text-xs md:text-sm")}
            {...props}
         >
            {children}
         </code>
      ) : (
         <div className="my-4 rounded-md overflow-hidden bg-[#1E1E1E] p-4">
            <SyntaxHighlighter
               language={match?.[1] || ""}
               style={syntaxTheme}
               className="!m-0 text-[11px] md:text-sm"
               showLineNumbers
            >
               {String(children).replace(/\n$/, "")}
            </SyntaxHighlighter>
         </div>
      );
   },
   p({ children, ...props }: any) {
      return (
         <p className="text-sm md:text-base leading-relaxed" {...props}>
            {children}
         </p>
      );
   },
   ul({ children, ...props }: any) {
      return (
         <ul className="mb-2 list-disc text-sm md:text-base" {...props}>
            {children}
         </ul>
      );
   },

   ol({ children, ...props }: any) {
      return (
         <ol className="pl-2 text-sm md:text-base" {...props}>
            {children}
         </ol>
      );
   },
   li({ children, ...props }: any) {
      return (
         <li className="ml-2" {...props}>
            {children}
         </li>
      );
   },
   h1({ children }: any) {
      return <h1 className="mb-4 text-xl md:text-2xl font-bold">{children}</h1>;
   },
   h2({ children }: any) {
      return <h2 className="mb-3 text-lg md:text-xl font-semibold">{children}</h2>;
   },
   h3({ children }: any) {
      return <h3 className="mb-2 text-base md:text-lg font-medium">{children}</h3>;
   },
   a({ href, children, ...props }: any) {
      const url = (href as string) || "";
      const isGoogleDoc =
         /https:\/\/docs\.google\.com\/(?:spreadsheets|document)\/d\//.test(url);

      if (isGoogleDoc) {
         return <GoogleDocEmbed url={url} />;
      }

      const hasNoChildren =
         !children ||
         (Array.isArray(children) && children.length === 0) ||
         (typeof children === "string" && children.trim() === "");

      return (
         <a
            className="break-all"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            {...props}
         >
            {hasNoChildren ? url : children}
         </a>
      );
   },
   img({ src, alt, ...props }: any) {
      return <MarkdownImage src={src} alt={alt} {...props} />;
   }
};

const GoogleDocEmbed = ({ url }: { url: string }) => {
   const spreadsheetRegex =
      /https:\/\/docs\.google\.com\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/;
   const documentRegex = /https:\/\/docs\.google\.com\/document\/d\/([a-zA-Z0-9_-]+)/;

   let embedUrl = "";
   let title = "Google Embed";
   const sheetMatch = url.match(spreadsheetRegex);
   const docMatch = url.match(documentRegex);
   if (sheetMatch) {
      const id = sheetMatch[1];
      const gidRegex = /[#&]gid=([0-9]+)/;
      const gidMatch = url.match(gidRegex);
      const gidParam = gidMatch ? `&gid=${gidMatch[1]}` : "";
      embedUrl = `https://docs.google.com/spreadsheets/d/${id}/preview?rm=minimal${gidParam}`;
      title = "Google Spreadsheet Embed";
   } else if (docMatch) {
      const id = docMatch[1];
      embedUrl = `https://docs.google.com/document/d/${id}/preview`;
      title = "Google Document Embed";
   }

   if (!embedUrl) return null;

   return (
      <>
         <a href={url} target="_blank" rel="noopener noreferrer">
            Reference link
         </a>
         <iframe
            src={embedUrl}
            title={title}
            width="100%"
            style={{
               border: "none",
               minHeight: "600px",
               margin: "10px 0",
               display: "block"
            }}
         ></iframe>
      </>
   );
};

const PlainTextRenderer = ({ content }: { content: string }) => {
   const urlRegex =
      /(https:\/\/docs\.google\.com\/(?:spreadsheets|document)\/d\/[a-zA-Z0-9_-]+(?:[^\s\n]*)?)/g;
   const parts = content.split(urlRegex);

   return (
      <>
         {parts.map((part, index) => {
            if (!part) return null;
            const key = uuidv7();
            if (index % 2 === 1) {
               return <GoogleDocEmbed key={key} url={part} />;
            } else {
               return <React.Fragment key={key}>{part}</React.Fragment>;
            }
         })}
      </>
   );
};

const PLACEHOLDER_TOKEN_REGEX = /\[\[(CHART|TABLE|COIN):([^\]]+)\]\]/g;

type ChatMessageItemProps = {
   msg: ChatMessage;
   style?: React.CSSProperties;
   shareKey?: string | null;
   isShowNote?: boolean;
   canResend?: boolean;
};

const reconstructPlaceholderRegistry = (
   metaData: Record<string, any>[]
): PlaceholderRegistry => {
   const registry: PlaceholderRegistry = {};
   if (!Array.isArray(metaData)) return registry;

   metaData.forEach((item) => {
      const isChartFunction =
         item.functionName === FUNCTION_NAME_ENUM.getTaiFu30CryptoIndexChartData ||
         item.functionName === FUNCTION_NAME_ENUM.getSpecificCoinChartData;

      if (isChartFunction) {
         const symbol = item.result?.summary?.symbol;
         if (symbol) {
            registry[`CHART:${symbol}`] = {
               type: "chart",
               functionName: item.functionName,
               symbol: symbol,
               apiUrl: item.result?.apiUrl,
               name: item.result?.summary?.name,
               description: item.result?.description
            };
         }
      } else if (
         item.functionName === FUNCTION_NAME_ENUM.getTop30CoinsTaiFuIndexByIndex
      ) {
         const indexCode = item.arguments?.indexCode || "TOP30";
         registry[`TABLE:${indexCode}`] = {
            type: "table",
            functionName: item.functionName,
            symbol: indexCode,
            result: item.result
         };
      }
   });
   return registry;
};

const ChatMessageItem = ({
   msg,
   style,
   shareKey,
   isShowNote = false,
   canResend = false
}: ChatMessageItemProps) => {
   const { user } = useAuth();
   const translate = useTranslations("Chat");
   const { selectedBaseKnowledge } = useAiSourceSelector();

   const noteTranslationKey = useMemo(() => {
      if (selectedBaseKnowledge?.id === PROJECTS.KINGS_AND_ALLIES) {
         return "noteForChat_kingsandallies";
      }
      if (selectedBaseKnowledge?.id === PROJECTS.CRYPTOCURRENCY_MARKET) {
         return "noteForChat_cryptocurrencymarket";
      }
      return "noteForChat";
   }, [selectedBaseKnowledge?.id]);
   const showModelName = useMemo(() => {
      if (user?.isAdmin && msg.modelName && msg.role === "assistant") {
         return msg.modelName;
      }
      return null;
   }, [user?.isAdmin, msg.modelName, msg.role]);

   const showExpandedQueryDebug = useMemo(() => {
      if (user?.isAdmin && msg.role === "assistant" && msg.expandedQuery) {
         return msg.expandedQuery;
      }
      return null;
   }, [user?.isAdmin, msg.role, msg.expandedQuery]);

   const metaData: Record<string, any>[] | [] = useMemo(() => {
      if (msg.role === "assistant" && msg.metaData) {
         return msg.metaData as Record<string, any>[];
      }
      return [];
   }, [msg.metaData, msg.role]);

   const effectiveRegistry = useMemo(() => {
      if (msg.placeholderRegistry && Object.keys(msg.placeholderRegistry).length > 0) {
         return msg.placeholderRegistry;
      }
      return reconstructPlaceholderRegistry(metaData);
   }, [msg.placeholderRegistry, metaData]);

   const hasPlaceholderTokensInContent = useMemo(() => {
      PLACEHOLDER_TOKEN_REGEX.lastIndex = 0;
      return PLACEHOLDER_TOKEN_REGEX.test(msg.content);
   }, [msg.content]);

   const hasPlaceholders = useMemo(() => {
      return Object.keys(effectiveRegistry).length > 0 && hasPlaceholderTokensInContent;
   }, [effectiveRegistry, hasPlaceholderTokensInContent]);

   const renderMarkdownContent = React.useCallback((content: string) => {
      const processedContent = preprocessMathContent(content);
      return processedContent;
   }, []);

   const messageContent = useMemo(() => {
      if (msg.role !== "assistant") {
         const isAttachmentOnlyPrompt =
            Boolean(msg.attachedFiles?.length) && msg.content === ATTACHMENT_ONLY_PROMPT;

         return (
            <>
               {!isAttachmentOnlyPrompt && (
                  <CollapsibleMessageContent className="whitespace-pre-wrap break-words text-[15px] leading-relaxed">
                     <PlainTextRenderer content={msg.content} />
                  </CollapsibleMessageContent>
               )}
               <ChatAttachments files={msg.attachedFiles} />
            </>
         );
      }

      if (hasPlaceholders) {
         const rawContent = msg.content;
         const contentSegments: Array<{
            type: "text" | "placeholder";
            content: string;
            placeholderId?: string;
         }> = [];
         let processedUpToIndex = 0;
         let tokenMatch: RegExpExecArray | null;

         PLACEHOLDER_TOKEN_REGEX.lastIndex = 0;
         while ((tokenMatch = PLACEHOLDER_TOKEN_REGEX.exec(rawContent)) !== null) {
            const textBeforeToken = rawContent.slice(
               processedUpToIndex,
               tokenMatch.index
            );
            if (textBeforeToken) {
               contentSegments.push({ type: "text", content: textBeforeToken });
            }

            const placeholderId = `${tokenMatch[1]}:${tokenMatch[2]}`;
            contentSegments.push({
               type: "placeholder",
               content: placeholderId,
               placeholderId
            });
            processedUpToIndex = tokenMatch.index + tokenMatch[0].length;
         }

         const remainingText = rawContent.slice(processedUpToIndex);
         if (remainingText) {
            contentSegments.push({ type: "text", content: remainingText });
         }

         const containsInlineComponents = contentSegments.some(
            (segment) => segment.type === "placeholder"
         );
         if (containsInlineComponents) {
            return (
               <>
                  {contentSegments.map((segment, idx) => {
                     if (segment.type === "placeholder" && segment.placeholderId) {
                        const placeholderData = effectiveRegistry[segment.placeholderId];
                        if (!placeholderData) {
                           return null;
                        }
                        return (
                           <InlinePlaceholder key={`ph-${idx}`} data={placeholderData} />
                        );
                     }
                     return (
                        <ReactMarkdown
                           key={`md-${idx}`}
                           className="prose prose-sm md:prose-base dark:prose-invert max-w-full break-words"
                           remarkPlugins={[remarkGfm]}
                           rehypePlugins={[rehypeRaw]}
                           components={MarkdownComponents}
                        >
                           {renderMarkdownContent(segment.content)}
                        </ReactMarkdown>
                     );
                  })}
               </>
            );
         }
      }

      // Default: standard markdown rendering (no placeholders)
      // Strip raw placeholder tokens from old messages that don't have a registry
      const sanitizedContent = msg.content.replace(
         /\[\[(CHART|TABLE|COIN):[^\]]+\]\]\n?/g,
         ""
      );
      return (
         <ReactMarkdown
            className="prose prose-sm md:prose-base dark:prose-invert max-w-full break-words"
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={MarkdownComponents}
         >
            {renderMarkdownContent(sanitizedContent)}
         </ReactMarkdown>
      );
   }, [
      msg.attachedFiles,
      msg.content,
      msg.role,
      effectiveRegistry,
      hasPlaceholders,
      renderMarkdownContent
   ]);

   return (
      <div className="group" style={style}>
         <div
            className={cn("flex", {
               "justify-start": msg.role === "assistant",
               "justify-end mr-2 pt-4 sm:mr-0": msg.role !== "assistant"
            })}
         >
            <div
               className={cn(
                  "max-w-[100%] rounded-lg sm:rounded-2xl p-1.5 sm:p-3 overflow-x-auto overflow-y-hidden",
                  {
                     "bg-white dark:bg-gray-800": msg.role === "assistant",
                     "transition-colors bg-accent-foreground/5 sm:max-w-[90%]":
                        msg.role !== "assistant"
                  }
               )}
            >
               <div>{messageContent}</div>
               {!hasPlaceholders && <ChatMessageMetadata metaData={metaData} />}

               {(showExpandedQueryDebug || showModelName) && (
                  <div className="mt-2 border-t border-foreground/10 pt-2 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                     {showExpandedQueryDebug ? (
                        <ExpandedQueryDebug expandedQuery={showExpandedQueryDebug} />
                     ) : (
                        <div className="hidden sm:block sm:flex-1" />
                     )}

                     {showModelName && (
                        <div className="text-[10px] sm:text-xs text-muted-foreground opacity-60 break-words sm:whitespace-nowrap sm:shrink-0 sm:ml-auto">
                           {`${showModelName} (${format(
                              new Date(msg?.timestamp ?? 0),
                              "yyyy/MM/dd HH:mm:ss"
                           )})`}
                        </div>
                     )}
                  </div>
               )}
            </div>
         </div>

         {isShowNote && (
            <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center bg-[#eaebebae] dark:bg-[#3d3d3dae] p-2 mt-2 rounded-xl max-w-[90%] sm:max-w-[85%] ">
               <Info strokeWidth="1.5px" size="17px" className="mr-1" />
               <div>{translate(noteTranslationKey)}</div>
            </div>
         )}

         {msg.role === "assistant" && (
            <div className="flex justify-start mt-2 ml-2 space-x-4 transition-opacity group-hover:opacity-100">
               <CopyContent msg={msg} />
               {msg.id && !shareKey && <MessageFeedback message={msg} />}
            </div>
         )}

         {msg.role !== "assistant" && (
            <div
               className={cn(
                  "flex justify-end mt-2 mr-2 transition-opacity",
                  !canResend && "opacity-0 group-hover:opacity-100"
               )}
            >
               <CopyContent msg={msg} />
               {canResend && (
                  <Button
                     variant="ghost"
                     size="xs"
                     className="w-9 px-0 rounded-xl text-muted-foreground hover:bg-primary/10 hover:text-primary"
                     aria-label={translate("resend")}
                     title={translate("resend")}
                     onClick={(event) =>
                        useStateHistoryChatPage
                           .getState()
                           .triggerSendMessageEvent(event, msg.content)
                     }
                  >
                     <RotateCw className="w-4 h-4" />
                  </Button>
               )}
            </div>
         )}
      </div>
   );
};

// A whole topic renders at once: without memo, every history change re-runs
// markdown + syntax highlighting for all messages.
export default React.memo(ChatMessageItem);
